import React, { useState } from "react";
import styled from "@emotion/styled";
import { useHistory } from "react-router";
import ServiceWrapper from "../components/ServiceWrapper";
import { track } from "../utils/track";
import { noPurchaseUI } from "../utils/billing";

/**
 * 자주 묻는 질문 (2026-10-10 사장님 승인 a-qa-faq, 문구 승인 a-qa-faq-text).
 * 예전엔 메뉴 FAQ 가 네이버 카페 글로만 나가고 비회원 메뉴에는 없었다. 카페 글은 맨 아래 '더 보기'로 남김.
 * 구독 답변은 지금 결제 방식(페이소프트 카카오 간편동의) 기준 — 카드 결제가 열리면 고칠 것.
 */
const FAQS: { q: string; a: string }[] = [
  { q: "코드스코어는 어떤 서비스인가요?", a: "기타·우쿨렐레로 노래를 칠 수 있게 코드 악보를 보여 주는 서비스예요. 원곡을 재생하면 지금 치는 마디가 따라 움직여요. 휴대폰·태블릿·컴퓨터 어디서나 chordscore.com 에서 쓸 수 있어요." },
  { q: "가입은 어떻게 하나요?", a: "메뉴(휴대폰은 아래쪽 '내 정보', 컴퓨터는 오른쪽 위 메뉴 단추)에서 '로그인'을 누르고 카카오로 시작하거나 '이메일로 회원가입'을 눌러 주세요. 비밀번호를 잊으셨으면 로그인 화면의 '비밀번호 찾기'를 이용해 주세요." },
  { q: "가입하지 않아도 쓸 수 있는 건 무엇인가요?", a: "곡 검색, 인기 악보·새로 올라온 악보 목록, 핫클립(연주 영상), 튜너는 가입 없이 쓸 수 있어요. 악보 전체를 보려면 구독이 필요해요." },
  { q: "구독은 얼마이고 어떻게 하나요?", a: "월 3,300원으로 모든 악보를 마음껏 볼 수 있어요.\n1) 로그인 후 메뉴에서 '구독 신청'을 누르세요. (처음이면 휴대폰 인증을 한 번 해요)\n2) 신청하면 바로 모든 악보가 열려요.\n3) 2일 안에 카카오톡으로 페이소프트(업체명: 에스비) 간편동의 요청이 와요. 동의하시면 결제가 돼요.\n4) 요청을 받고 3일 안에 결제가 되지 않으면 구독이 취소돼요." },
  { q: "구독을 해지하려면요?", a: "로그인 후 메뉴에서 '구독 해지'를 누르세요. 해지해도 마지막 결제일로부터 한 달까지는 악보를 볼 수 있어요." },
  { q: "키를 바꾸거나 카포를 쓰고 싶어요.", a: "곡 화면 아래 조절판에서 키를 올리고 내릴 수 있어요. '쉬운 코드로 치기'에서 카포 위치를 고르면 원곡과 같은 높이로 더 쉬운 코드 모양을 보여 줘요. '코드 단순하게'를 켜면 Cmaj7 → C 처럼 초보용 코드로 바뀌어요." },
  { q: "악보가 작게 보이거나 보기 방식을 바꾸고 싶어요.", a: "곡 화면에서 '글자 크기'로 크기를 바꿀 수 있고, 고른 크기는 다음에도 기억해요. '마디 보기'와 '가사 보기'(가사 위에 코드만) 중 편한 쪽을 고르세요. 반주 버튼을 누르면 기타 반주 소리도 함께 들을 수 있어요." },
  { q: "앱이 있나요?", a: "안드로이드는 앱 소개 페이지(chordscore.com/download)에서 내려받을 수 있어요. 아이폰은 사파리에서 공유 버튼 → '홈 화면에 추가'를 하면 앱처럼 쓸 수 있어요." },
  { q: "치고 싶은 곡이 없어요.", a: "'신청곡' 화면에서 곡 제목과 가수를 남겨 주시면 악보를 만들어 올려 드려요. 올라오면 알림으로 알려 드려요." },
  { q: "악보가 틀린 것 같아요.", a: "곡 화면의 '악보 수정 요청'으로 어디가 틀렸는지 알려 주세요. 확인해서 고쳐 드려요." },
  { q: "다른 궁금한 점은 어디에 물어보나요?", a: "로그인 후 메뉴의 '문의/요청사항'에 남겨 주시면 답변드려요. 네이버 카페(cafe.naver.com/chordscore)에서도 이야기를 나눌 수 있어요." },
];
// 앱(웹뷰) 안에서는 구독·가격 질문을 숨긴다 — 애플 3.1.1 재심사 (2026-10-16)
const appFaqs = () =>
  noPurchaseUI() ? FAQS.filter((f) => !f.q.includes("구독")).map((f) => ({ ...f, a: f.a.replace(" 악보 전체를 보려면 구독이 필요해요.", "") })) : FAQS;

const Faq: React.FC = () => {
  const history = useHistory();
  const [open, setOpen] = useState<number | null>(null);
  const faqs = appFaqs();

  return (
    <ServiceWrapper isFooterRendered={false}>
      <Page>
        <h1>자주 묻는 질문</h1>
        <List>
          {faqs.map((f, i) => {
            const on = open === i;
            return (
              <li key={i} className={on ? "on" : ""}>
                <button
                  type="button"
                  aria-expanded={on}
                  aria-controls={`faq-a-${i}`}
                  onClick={() => {
                    setOpen(on ? null : i);
                    if (!on) track(`faq:open:${i + 1}`);
                  }}
                >
                  <span className="q">Q</span>
                  <span className="t">{f.q}</span>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
                {on && <p id={`faq-a-${i}`}>{f.a}</p>}
              </li>
            );
          })}
        </List>
        <More>
          <p>찾는 답이 없나요?</p>
          <button type="button" className="primary" onClick={() => history.push("/support")}>문의하기</button>
          <button type="button" onClick={() => window.open("https://cafe.naver.com/chordscore/653", "_blank")}>카페 FAQ 더 보기</button>
        </More>
      </Page>
    </ServiceWrapper>
  );
};

export default Faq;

const Page = styled.div`
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 16px 16px 110px;
  color: #f3f4f6;
  & > h1 {
    margin: 0 0 16px;
    font-size: 24px;
    font-weight: 700;
    color: #f3f4f6;
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #2a2e38;
  border-radius: 14px;
  overflow: hidden;
  background: #16181e;
  & > li + li {
    border-top: 1px solid #2a2e38;
  }
  & button {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 56px;
    padding: 12px 14px;
    border: 0;
    background: transparent;
    color: #f3f4f6;
    font-size: 15px;
    font-weight: 500;
    text-align: left;
    cursor: pointer;
    word-break: keep-all;
  }
  & .q {
    flex: none;
    color: #ffb547;
    font-weight: 700;
  }
  & .t {
    flex: 1;
    line-height: 1.45;
  }
  & svg {
    flex: none;
    width: 20px;
    height: 20px;
    color: #7c8392;
    transition: transform 0.18s ease;
  }
  & li.on svg {
    transform: rotate(180deg);
    color: #ffb547;
  }
  & p {
    margin: 0;
    padding: 0 16px 16px 38px;
    font-size: 14px;
    line-height: 1.7;
    color: #c4c9d4;
    white-space: pre-line;
    word-break: keep-all;
  }
`;

const More = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
  & > p {
    margin: 0 0 4px;
    font-size: 14px;
    color: #a3a9b5;
    text-align: center;
  }
  & > button {
    height: 50px;
    border-radius: 14px;
    border: 1px solid #2a2e38;
    background: transparent;
    color: #f3f4f6;
    font-size: 15px;
    cursor: pointer;
  }
  & > button.primary {
    border: 0;
    background: #ffb547;
    color: #0d0e12;
    font-weight: 700;
  }
`;
